import { FC, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Box, Flex } from '@rebass/emotion';
import { useParams } from 'react-router-dom';

import Loader from '@app/components/Loader';

import { isNotNullish } from '@app/helpers/predicate';
import { AlertTitle, AlertType } from '@app/shop/components/Alert';
import Alerts from '@app/shop/components/Alerts';
import useAlerts from '@app/shop/hooks/useAlerts';
import {
  pushEcommerceEvent,
  pushFunnelEvent,
  pushData,
} from '@app/shop/tracking';
import { convertToEuro } from '@app/shop/helpers';
import Support from '@app/shop/components/Support/Support';
import useGTMTracking from '@app/shop/hooks/useGTMTracking';

import {
  CheckoutSuccessQuery,
  CheckoutSuccessQuery_user,
  CheckoutSuccessQuery_magento2_cmsBlocks_items as Notice,
} from './__generated__/CheckoutSuccessQuery';
import {
  CheckoutOrderQuery,
  CheckoutOrderQueryVariables,
  CheckoutOrderQuery_magento2_customer_globalOrders_items as Order,
  CheckoutOrderQuery_magento2_customer_globalOrders_items_items as OrderItem,
} from './__generated__/CheckoutOrderQuery';
import { SUCCESS_QUERY, ORDER_QUERY } from './Success.queries';
import OrderStatus from './OrderStatus';
import OrderReview from './OrderReview';
import NextSteps from './NextSteps';

const Success: FC = () => {
  const { alerts, addAlerts } = useAlerts();

  const { orderNumber } = useParams<{ orderNumber: string }>();

  const { data: orderData, loading: orderLoading } = useQuery<
    CheckoutOrderQuery,
    CheckoutOrderQueryVariables
  >(ORDER_QUERY, {
    variables: { orderNumber },
    errorPolicy: 'all',
    ssr: false,
  });

  const {
    data: successData,
    loading: successLoading,
  } = useQuery<CheckoutSuccessQuery>(SUCCESS_QUERY, {
    errorPolicy: 'all',
    ssr: false,
    onError: (error) => {
      if (!error.message.includes('CMS')) {
        // M2 errors out when cms block is disabled, this ignores this error
        addAlerts([
          {
            type: AlertType.Error,
            content: <AlertTitle>{error.message}</AlertTitle>,
          },
        ]);
      }
    },
  });

  const customer = successData?.magento2?.customer;
  const cmsNotices = successData?.magento2?.cmsBlocks?.items?.filter(
    isNotNullish
  );
  const user = successData?.user;
  const order = orderData?.magento2?.customer?.globalOrders?.items[0];
  const storeConfig = orderData?.magento2?.storeConfig;

  useGTMTracking({ step: 'success' });

  useEffect(() => {
    if (order && user) {
      pushTransactionEvent(order, user);
    }
  }, [order, user]);

  const virtualItems = order?.items
    ?.filter((item) => isVirtualItem(order, item))
    .filter(isNotNullish);

  const virtualProductName =
    (virtualItems?.length === 1 && virtualItems?.[0]?.productName) || '';

  const notices = useMemo(
    () => cmsNotices && getOrderNotices(order?.items, virtualItems, cmsNotices),
    [order, cmsNotices, virtualItems]
  );

  if (successLoading || orderLoading) {
    return (
      <Flex justifyContent="center">
        <Loader />
      </Flex>
    );
  }

  return (
    <>
      <Box mb={20}>
        <Alerts data-testid="alerts" alerts={alerts} />
      </Box>

      {order && customer && (
        <Box my={40}>
          <OrderStatus customer={customer} />
        </Box>
      )}

      {order && storeConfig && (
        <Box my={40}>
          <Flex my={40} flexWrap="wrap">
            <OrderReview
              order={order}
              storeConfig={storeConfig}
              notices={notices}
            />
          </Flex>
        </Box>
      )}

      {!!virtualItems?.length && (
        <Box my={40}>
          <NextSteps productName={virtualProductName} />
        </Box>
      )}

      <Box my={40}>
        <Support />
      </Box>
    </>
  );
};

export default Success;

const isVirtualItem = (order: Order | null, item: OrderItem | null): boolean =>
  (order?.isVirtual && item?.productType === 'bundle') ||
  item?.productType === 'virtual';

const getOrderNotices = (
  items: (OrderItem | null)[] | null | undefined,
  virtualProducts: (OrderItem | null)[] | null | undefined,
  cmsNotices: Notice[]
): Notice[] => {
  const getNotice = (id: string) =>
    cmsNotices.find((notice) => notice.identifier === id) || null;

  const hardwareProducts = items
    ?.filter((item) => item?.productType === 'simple')
    .filter(isNotNullish);

  return [
    getNotice('success-general-notice'),
    virtualProducts?.length ? getNotice('success-software-notice') : null,
    hardwareProducts?.length ? getNotice('success-hardware-notice') : null,
  ].filter(isNotNullish);
};

export const pushTransactionEvent = (
  order: Order,
  user: CheckoutSuccessQuery_user
): void => {
  let discountLabels = '';
  const discountsSum =
    order.total?.discounts?.reduce((sum, discount) => {
      discountLabels += `${discount?.label};`;
      return discount?.amount.value ? sum + discount.amount.value : sum;
    }, 0) ?? 0;

  const totalEUR = convertToEuro(
    order.total?.grandTotal.value,
    order.total?.grandTotal.currency
  );

  // NOTE: After comparison with M1, only transactionTotal, transactionProducts > price
  // and purchase should be converted to EUR.
  pushData({
    event: 'transaction',
    transactionId: order?.id,
    transactionType: 'purchase',
    transactionAffiliation: 'Native Instruments',
    transactionTotal: totalEUR,
    transactionTax: order.total?.totalTax.value,
    transactionShipping: order.total?.totalShipping.value,
    transactionPaymentType: order.paymentMethods?.[0]?.name,
    transactionCurrency: order.total?.grandTotal.currency,
    transactionProducts: order.items?.map((item) => ({
      id: item?.id,
      sku: item?.productSku,
      master: item?.productSku,
      name: item?.productName,
      price: convertToEuro(
        item?.productSalePrice.value,
        item?.productSalePrice.currency
      ),
      quantity: item?.quantityOrdered,
    })),
    transactionCity: order.billingAddress?.city,
    transactionState: order.billingAddress?.region,
    transactionCountry: order.billingAddress?.countryCode,
    lang: user.language,
    total: order.total?.grandTotal?.value,
    subtotal: order.total?.subtotal.value,
    purchase: totalEUR,
    discount: discountsSum,
    storeId: order.storeCode,
    uniqueUserHash: user.hash,
  });

  pushEcommerceEvent('Purchase', {
    purchase: {
      actionField: {
        id: order.id,
        affiliation: 'Native Instruments',
        revenue: convertToEuro(
          order.total?.grandTotal.value,
          order.total?.grandTotal.currency
        ),
        tax: order.total?.totalTax.value,
        shipping: order.total?.totalShipping.value,
        coupon: discountLabels,
      },
      products: order.items?.map((item) => ({
        id: item?.id,
        sku: item?.productSku,
        master: item?.productSku,
        name: item?.productName,
        price: convertToEuro(
          item?.productSalePrice.value,
          item?.productSalePrice.currency
        ),
        quantity: item?.quantityOrdered,
        brand: item?.productType,
        category: item?.productType,
      })),
    },
  });

  pushFunnelEvent(
    'success',
    order.items?.map((item) => ({
      productId: item?.productSku,
      productName: item?.productName,
      price: convertToEuro(
        item?.productSalePrice.value,
        item?.productSalePrice.currency
      ),
      quantity: item?.quantityOrdered,
      category: '',
      subCategory: '',
    }))
  );
};
